import React, { useMemo, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import {
  BlockTitle,
  Row,
  Col,
  Button,
  OutlinedInput,
  RSelect,
} from "../../components/Component";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import FormWithLabel from "../../components/input/label/FormWithLabel";
import { useBeManualFetcher } from "../../hooks/useBeManualFetcher";
import { useAuthContext } from "../../hooks/useAuthContext";
import { USER_ROLES } from "../../utilities/constants";
import userService from "../../services/userService";

const canManageUsers = (roles) =>
  roles?.some(
    (r) => r === USER_ROLES.DEVELOPER || r === USER_ROLES.ADMIN
  ) ?? false;

const UserCreatePage = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { errors, register, handleSubmit } = useForm();
  const [onFetch, isLoading] = useBeManualFetcher();

  const [selectedRoles, setSelectedRoles] = useState([]);

  useEffect(() => {
    if (user && !canManageUsers(user?.roles)) {
      navigate("/users", { replace: true });
    }
  }, [user, navigate]);

  const roleOptions = useMemo(
    () =>
      Object.values(USER_ROLES).map((role) => ({
        value: role,
        label: role,
      })),
    [],
  );

  async function onFormSubmit(form) {
    const roles = (selectedRoles ?? []).map((r) => r.value);

    const requestBody = {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      password: form.password,
      roles,
    };

    return await onFetch({
      action: async () => await userService.createUser(requestBody),
      onLoad: () => navigate("/users", { replace: true }),
      successMessage: "User created successfully",
      fetchingItemId: "create-user",
    });
  }

  const isSaveLoading =
    typeof isLoading === "object" && isLoading?.id === "create-user"
      ? isLoading?.isLoading
      : false;

  return (
    <React.Fragment>
      <Head title="Create User" />
      <Content>
        <Button
          color="white border-light"
          onClick={() => navigate("/users")}
          iconName="arrow-left"
          title="Back"
        />
        <BlockTitle tag="h5" className="my-3">
          Create User
        </BlockTitle>

        <form onSubmit={handleSubmit(onFormSubmit)}>
          <Row className="g-3">
            <Col size="6">
              <FormWithLabel label="First Name" noWrap>
                <OutlinedInput
                  type="text"
                  className="form-control"
                  label="First Name"
                  name="firstName"
                  forwardRef={register({
                    required: "This field is required",
                  })}
                  errors={errors}
                />
              </FormWithLabel>
            </Col>
            <Col size="6">
              <FormWithLabel label="Last Name" noWrap>
                <OutlinedInput
                  type="text"
                  className="form-control"
                  label="Last Name"
                  name="lastName"
                  forwardRef={register({
                    required: "This field is required",
                  })}
                  errors={errors}
                />
              </FormWithLabel>
            </Col>
            <Col size="6">
              <FormWithLabel label="Email" noWrap>
                <OutlinedInput
                  type="email"
                  className="form-control"
                  label="Email"
                  name="email"
                  forwardRef={register({
                    required: "This field is required",
                  })}
                  errors={errors}
                />
              </FormWithLabel>
            </Col>
            <Col size="6">
              <FormWithLabel label="Password" noWrap>
                <OutlinedInput
                  type="password"
                  className="form-control"
                  label="Password"
                  name="password"
                  forwardRef={register({
                    required: "This field is required",
                  })}
                  errors={errors}
                />
              </FormWithLabel>
            </Col>
            <Col size="12">
              <FormWithLabel label="Roles" noWrap>
                <RSelect
                  options={roleOptions}
                  onChange={(e) => setSelectedRoles(e)}
                  value={selectedRoles}
                  isMulti
                />
              </FormWithLabel>
            </Col>
            <Col size="12" className="text-right">
              <Button
                color="primary"
                type="submit"
                iconName="save"
                title="Save"
                isLoading={isSaveLoading}
              />
            </Col>
          </Row>
        </form>
      </Content>
    </React.Fragment>
  );
};

export default UserCreatePage;

